import * as yup from 'yup'

import { SettingType } from '../../types/settings'

export interface ISettingsAdapterArgs<RawType, UiType, UiInitial> {
  inputName: string
  variable: string
  type: SettingType
  defaultRawValue: RawType
  initialUiValue: UiType | UiInitial
  toUiValue: (rawValue: RawType) => UiType
  toRawValue: (uiValue: UiType) => RawType
  skipFormPopulateOnLoad: boolean
  shouldUseRawValue: (rawValue: RawType) => boolean
  shouldTreatAsUnset: (rawValue: RawType) => boolean
  schema: yup.Schema
}

export const SettingsAdapter = <RawType, UiType, UiInitial>({
  inputName,
  variable,
  type,
  defaultRawValue,
  initialUiValue,
  toUiValue,
  toRawValue,
  skipFormPopulateOnLoad,
  shouldUseRawValue,
  shouldTreatAsUnset,
  schema
}: ISettingsAdapterArgs<RawType, UiType, UiInitial>) => {
  let useDefaultRawValue = true
  let currentRawValue: RawType
  let currentUiValue: UiType | UiInitial

  const getRawValue = () => currentRawValue

  const setRawValue = (rawValue: RawType) => {
    useDefaultRawValue = false
    currentRawValue = rawValue
    currentUiValue = toUiValue(rawValue)
  }

  const getUiValue = () =>
    useDefaultRawValue || !shouldUseRawValue(currentRawValue) ? toUiValue(defaultRawValue) : currentUiValue

  const setUiValue = (value: UiType) => {
    if (typeof value === 'undefined') {
      return
    }
    useDefaultRawValue = false
    currentUiValue = value
    currentRawValue = toRawValue(value)
  }

  return Object.freeze({
    variable,
    type,
    initialUiValue,
    getRawValue,
    setRawValue,
    getUiValue,
    setUiValue,
    inputName,
    skipFormPopulateOnLoad,
    shouldUseRawValue,
    shouldTreatAsUnset,
    schema
  })
}

export type ISettingsAdapter<RawType, UiType, UiInitial> = ReturnType<
  typeof SettingsAdapter<RawType, UiType, UiInitial>
>
